import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { trackCreate, useOpenedGoal } from "@/lib/metrica";
import { useSetupReturn } from "@/lib/setupFlow";
import { MOBILE_STICKY_FOOTER } from "@/lib/utils";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState, StatCard, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, Pencil, Trash2, Eye, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import {
  InvoiceItemsEditor, serializeInvoiceItems, makeEmptyInvoiceItem,
} from "@/components/InvoiceItemsEditor";
import { BillingChoice } from "@/components/BillingChoice";

const EMPTY = {
  date: todayISO(),
  description: "",
  category: "Diğer",
  amount: "",
  currency: "TRY",
  cash_account: "",
  expense_type: "general",
  customer_id: "",
  job_id: "",
  notes: "",
  // Faturalandırma seçimi: "" (seçilmedi) | "invoiced" | "non_invoiced".
  billing: "",
  // Faturalı gider alanları — is_invoiced=false iken hepsi boş kalır.
  // Çoklu kalem (InvoiceItemsEditor) UI'daki tek yoldur; tek-satır
  // legacy `subtotal_ex_vat + vat_rate` yolu artık kullanılmaz.
  is_invoiced: false,
  invoice_date: "",
  invoice_number: "",
  invoice_items: null,
};

const CASH_LABEL = { company: "Şirket Kasası", personal: "Şahsi Kasa" };
const TYPE_LABEL = { general: "Genel Şirket Gideri", customer_job: "Müşteri / İş Gideri" };

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [cashFilter, setCashFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [e, c, j, cat, vr] = await Promise.all([
      api.get("/expenses"), api.get("/customers"), api.get("/jobs"),
      api.get("/categories"), api.get("/vat-rates?status=active"),
    ]);
    setItems(e.data); setCustomers(c.data); setJobs(j.data);
    setCategories(cat.data || []);
    setVatRates(vr.data || []);
  };
  useEffect(() => { load(); }, []);

  // "Yeni Gider" formu yalnızca aktif kategorileri gösterir.  Filtreleme
  // dropdown'ı ise eski kayıtlar için pasif olanları da içerir.
  const activeCategories = useMemo(
    () => (categories ?? []).filter((c) => c.status === "active"),
    [categories]
  );

  const jobsForSelectedCustomer = useMemo(() => {
    if (!form.customer_id) return jobs ?? [];
    return (jobs ?? []).filter((j) => j.customer_id === form.customer_id);
  }, [jobs, form.customer_id]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return (items ?? []).filter((it) => {
      const matchS = !s || [it.description, it.category, it.notes]
        .filter(Boolean).some((v) => String(v ?? "").toLowerCase().includes(s));
      const matchCat = categoryFilter === "all" || it.category === categoryFilter;
      const matchCash = cashFilter === "all" || it.cash_account === cashFilter;
      const matchType = typeFilter === "all" || it.expense_type === typeFilter;
      const matchFrom = !dateFrom || (it.date && it.date >= dateFrom);
      const matchTo = !dateTo || (it.date && it.date <= dateTo);
      return matchS && matchCat && matchCash && matchType && matchFrom && matchTo;
    });
  }, [items, search, categoryFilter, cashFilter, typeFilter, dateFrom, dateTo]);

  const totals = useMemo(() => {
    let total = 0;
    (filtered ?? []).forEach((i) => { total += +i.amount; });
    return { total, count: (filtered ?? []).length };
  }, [filtered]);

  const location = useLocation();
  const navigate = useNavigate();
  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  useSetupReturn("expense", open);
  // Kurulumdan ``?new=1`` ile geldiyse Yeni Gider dialog'unu aç.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("new") === "1") {
      openCreate();
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const onEdit = (i) => {
    setEditing(i);
    setForm({
      ...EMPTY,
      ...i,
      customer_id: i.customer_id || "",
      job_id: i.job_id || "",
      amount: String(i.amount ?? ""),
      is_invoiced: !!i.is_invoiced,
      billing: i.is_invoiced ? "invoiced" : "non_invoiced",
      invoice_date: i.invoice_date || "",
      invoice_number: i.invoice_number || "",
      // Legacy kayıtlar tek-oranlı olabilir; onları çoklu-kalem
      // UI'da tek satırlık bir kalem olarak açıyoruz ki kullanıcı
      // düzenleyebilsin.  Kaydedildiğinde server yeni items[] yolunu
      // yeniden hesaplar.
      invoice_items: Array.isArray(i.invoice_items) && i.invoice_items.length
        ? i.invoice_items.map((it) => ({
            description: it.description || "",
            quantity:   String(it.quantity ?? 1),
            unit_price: String(it.unit_price ?? 0),
            vat_rate:   String(it.vat_rate ?? 20),
          }))
        : (i.is_invoiced && i.subtotal_ex_vat != null
            ? [{
                description: i.description || "Fatura",
                quantity: "1",
                unit_price: String(i.subtotal_ex_vat),
                vat_rate: String(i.vat_rate ?? 20),
              }]
            : null),
    });
    setOpen(true);
  };

  const isInvoiced = form.billing === "invoiced";
  const selectBilling = (v) => setForm((f) => ({
    ...f,
    billing: v,
    invoice_date: v === "invoiced" ? (f.invoice_date || f.date) : f.invoice_date,
    invoice_items: v === "invoiced" && !(f.invoice_items && f.invoice_items.length)
      ? [makeEmptyInvoiceItem(vatRates)] : f.invoice_items,
    // Faturalı gider yalnızca Şirket Kasası'ndan; şahsi seçiliyse kullanıcı yeniden seçer.
    cash_account: v === "invoiced" && f.cash_account === "personal" ? "" : f.cash_account,
  }));

  const invoiceTotals = useMemo(() => {
    if (!isInvoiced || !Array.isArray(form.invoice_items)) {
      return { ex: 0, vat: 0, gt: 0 };
    }
    let ex = 0, vat = 0;
    for (const it of form.invoice_items) {
      const q = parseFloat(it.quantity)   || 0;
      const u = parseFloat(it.unit_price) || 0;
      const r = parseFloat(it.vat_rate)   || 0;
      const line = q * u;
      ex += line; vat += line * (r / 100);
    }
    return { ex, vat, gt: ex + vat };
  }, [isInvoiced, form.invoice_items]);

  useOpenedGoal("expense_create_opened", open && !editing);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Açıklama zorunludur"); return; }
    if (!form.category) { toast.error("Kategori seçin"); return; }
    if (!form.billing) { toast.error("Lütfen faturalandırma türünü seçin."); return; }
    if (!form.cash_account) { toast.error("Lütfen kasa seçimi yapın."); return; }

    if (isInvoiced) {
      if (!form.invoice_date) { toast.error("Fatura tarihini girin"); return; }
      if (!Array.isArray(form.invoice_items) || form.invoice_items.length === 0) {
        toast.error("En az bir fatura kalemi ekleyin"); return;
      }
      for (let i = 0; i < form.invoice_items.length; i++) {
        const it = form.invoice_items[i];
        if (!(it.description || "").trim()) {
          toast.error(`${i + 1}. kalem: Açıklama girin`); return;
        }
        if ((parseFloat(it.quantity) || 0) <= 0) {
          toast.error(`${i + 1}. kalem: Miktar 0'dan büyük olmalı`); return;
        }
        if ((parseFloat(it.unit_price) || 0) <= 0) {
          toast.error(`${i + 1}. kalem: Birim fiyat 0'dan büyük olmalı`); return;
        }
      }
    } else {
      const amt = parseFloat(form.amount);
      if (!amt || amt <= 0) { toast.error("Tutar 0'dan büyük olmalıdır"); return; }
    }

    try {
      const base = {
        date: form.date,
        description: form.description.trim(),
        category: form.category,
        currency: form.currency || "TRY",
        cash_account: form.cash_account,
        expense_type: form.expense_type,
        customer_id: form.expense_type === "customer_job" ? (form.customer_id || null) : null,
        job_id: form.expense_type === "customer_job" ? (form.job_id || null) : null,
        notes: form.notes || "",
        is_invoiced: isInvoiced,
      };
      const payload = isInvoiced
        ? {
            ...base,
            invoice_date: form.invoice_date,
            invoice_number: (form.invoice_number || "").trim() || null,
            invoice_items: serializeInvoiceItems(form.invoice_items),
            // Placeholder — server yeniden hesaplar.
            amount: invoiceTotals.gt,
          }
        : {
            ...base,
            amount: parseFloat(form.amount),
          };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await trackCreate("expense", () => api.post("/expenses", payload));
      toast.success(editing ? "Gider güncellendi" : "Gider kaydedildi. Kasadan tutar düşüldü.");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const onDelete = async (i) => {
    if (!window.confirm("Bu gider kaydı silinecek ve tutar ilgili kasaya geri eklenecek. Devam etmek istiyor musunuz?")) return;
    try {
      await api.delete(`/expenses/${i.id}`);
      toast.success("Gider silindi. Tutar kasaya geri eklendi.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silme başarısız");
    }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <StickyPageHead>
      <PageHeader
        title="Giderler"
        subtitle={<span className="max-md:hidden">Kasadan doğrudan yapılan giderler — kaydedildiğinde otomatik düşülür</span>}
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg" onClick={openCreate} data-testid="add-expense-button">
                <Plus className="w-4 h-4 mr-1.5" /> Yeni Gider
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto max-md:max-h-[calc(100dvh_-_1rem)] max-md:overflow-x-hidden">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Gider Düzenle" : "Yeni Gider"}</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="expense-form">
                <Field label="Tarih *" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required testId="expense-date-input" />
                <div className="space-y-1.5">
                  <Label className="text-xs">Gider Türü *</Label>
                  <Select value={form.expense_type} onValueChange={(v) => setForm({ ...form, expense_type: v, customer_id: "", job_id: "" })}>
                    <SelectTrigger className="h-10 rounded-lg" data-testid="expense-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Genel Şirket Gideri</SelectItem>
                      <SelectItem value="customer_job">Müşteri / İş Gideri</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs">Açıklama *</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="h-10 rounded-lg"
                    placeholder="Ör: Araç yakıtı, Ofis internet faturası..."
                    required
                    data-testid="expense-description-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Kategori *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="h-10 rounded-lg" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(activeCategories ?? []).map((c) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                      {editing && form.category && !(activeCategories ?? []).some((c) => c.name === form.category) && (
                        <SelectItem value={form.category}>{form.category} (Pasif)</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {form.expense_type === "customer_job" && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Müşteri (opsiyonel)</Label>
                      <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v, job_id: "" })}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="expense-customer-select"><SelectValue placeholder="(opsiyonel)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Yok —</SelectItem>
                          {(customers ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">İş / Proje (opsiyonel)</Label>
                      <Select value={form.job_id || "none"} onValueChange={(v) => setForm({ ...form, job_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="expense-job-select"><SelectValue placeholder="(opsiyonel)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Yok —</SelectItem>
                          {(jobsForSelectedCustomer ?? []).map((j) => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <BillingChoice className="md:col-span-2" testIdPrefix="expense-billing" value={form.billing}
                               onChange={selectBilling} nonInvoicedHint="Tutarı elle girin" />

                {isInvoiced && (
                  <div className="md:col-span-2 min-w-0 space-y-3 border border-slate-200 rounded-xl p-4 max-md:p-3 bg-slate-50/50" data-testid="expense-invoice-section">
                    <div className="text-sm font-medium text-slate-800">Fatura Bilgileri</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Field
                        label="Fatura Tarihi *" type="date" value={form.invoice_date}
                        onChange={(v) => setForm({ ...form, invoice_date: v })}
                        required testId="expense-invoice-date-input"
                      />
                      <Field
                        label="Fatura No" value={form.invoice_number}
                        onChange={(v) => setForm({ ...form, invoice_number: v })}
                        testId="expense-invoice-number-input"
                      />
                      <div className="text-[11px] text-slate-500 self-end pb-2">
                        Kalem başına farklı KDV oranı seçebilirsiniz.
                      </div>
                    </div>
                    <InvoiceItemsEditor
                      items={form.invoice_items && form.invoice_items.length
                        ? form.invoice_items
                        : [makeEmptyInvoiceItem(vatRates)]}
                      setItems={(next) => setForm({ ...form, invoice_items: next })}
                      vatRates={vatRates}
                      testIdPrefix="expense-invoice-item"
                      mobileCards
                    />
                  </div>
                )}

                <Field
                  label={isInvoiced ? "KDV Dahil Toplam (₺)" : "Tutar (₺) *"}
                  type="number"
                  step="0.01"
                  value={isInvoiced
                    ? String(invoiceTotals.gt.toFixed(2))
                    : form.amount}
                  onChange={(v) => setForm({ ...form, amount: v })}
                  disabled={form.billing !== "non_invoiced"}
                  required={form.billing === "non_invoiced"}
                  testId="expense-amount-input"
                />

                <div className="space-y-1.5">
                  <Label className="text-xs">Kasa *</Label>
                  <Select value={form.cash_account} onValueChange={(v) => setForm({ ...form, cash_account: v })}>
                    <SelectTrigger className="h-10 rounded-lg" data-testid="expense-cash-select"><SelectValue placeholder="Kasa seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">Şirket Kasası</SelectItem>
                      <SelectItem value="personal" disabled={isInvoiced}>Şahsi Kasa</SelectItem>
                    </SelectContent>
                  </Select>
                  {isInvoiced && (
                    <p className="text-[11px] text-slate-500" data-testid="expense-invoiced-cash-note">Faturalı giderler yalnızca Şirket Kasası'ndan yapılabilir.</p>
                  )}
                  {form.cash_account === "personal" && (
                    <p className="text-[11px] text-slate-500" data-testid="expense-personal-note">Şahsi kasadan yapılan giderler KDV raporuna dahil edilmez.</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">TRY (₺)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs">Not</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="expense-notes-input" />
                </div>

                <DialogFooter className={`md:col-span-2 ${MOBILE_STICKY_FOOTER} max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))]`} data-testid="expense-form-footer">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="expense-save-button">Kaydet</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      </StickyPageHead>

      <dl className="md:hidden rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-1.5 text-sm" data-testid="expenses-mobile-summary">
        <SummaryRow label="Listelenen Gider Sayısı" value={totals.count} testId="expenses-mobile-count" />
        <SummaryRow label="Listelenen Giderler Toplamı" value={fmtTRY(totals.total)} testId="expenses-mobile-total" />
        <SummaryRow label="Toplam Kayıt" value={(items ?? []).length} testId="expenses-mobile-records" />
      </dl>
      <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Listelenen Gider Sayısı" value={totals.count} accent="slate" testId="expenses-count" />
        <StatCard label="Listelenen Giderler Toplamı" value={fmtTRY(totals.total)} accent="rose" testId="expenses-total" />
        <StatCard label="Toplam Kayıt" value={(items ?? []).length} accent="slate" />
      </div>

      <div className="flex flex-wrap items-center gap-2 max-md:grid max-md:grid-cols-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm max-md:col-span-2 max-md:max-w-none max-md:min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Gider ara..." className="pl-9 h-10 rounded-lg" data-testid="expenses-search-input" />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-10 rounded-lg max-md:w-full max-md:min-w-0" data-testid="expenses-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-10 rounded-lg max-md:w-full max-md:min-w-0" data-testid="expenses-date-to" />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-44 h-10 rounded-lg max-md:w-full max-md:col-span-2" data-testid="expenses-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {(categories ?? []).map((c) => (
              <SelectItem key={c.id} value={c.name}>
                {c.name}{c.status === "passive" ? " (Pasif)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={cashFilter} onValueChange={setCashFilter}>
          <SelectTrigger className="w-40 h-10 rounded-lg max-md:w-full max-md:min-w-0" data-testid="expenses-cash-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kasalar</SelectItem>
            <SelectItem value="company">Şirket Kasası</SelectItem>
            <SelectItem value="personal">Şahsi Kasa</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48 h-10 rounded-lg max-md:w-full max-md:min-w-0" data-testid="expenses-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Gider Türleri</SelectItem>
            <SelectItem value="general">Genel Şirket Gideri</SelectItem>
            <SelectItem value="customer_job">Müşteri / İş Gideri</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <SectionCard>
        {(filtered ?? []).length === 0 ? <EmptyState title="Gider bulunamadı" /> : (
          <>
          <ul className="md:hidden divide-y divide-slate-100" data-testid="expense-cards">
            {(filtered ?? []).map((it) => (
              <ExpenseCard key={it.id} it={it} onView={() => setViewing(it)}
                           onEdit={() => onEdit(it)} onDelete={() => onDelete(it)} />
            ))}
          </ul>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Tarih</th>
                  <th className="px-5 py-3 font-semibold">Açıklama</th>
                  <th className="px-5 py-3 font-semibold">Kategori</th>
                  <th className="px-5 py-3 font-semibold">Gider Türü</th>
                  <th className="px-5 py-3 font-semibold">Müşteri / İş</th>
                  <th className="px-5 py-3 font-semibold">Kasa</th>
                  <th className="px-5 py-3 font-semibold text-right">Tutar</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(filtered ?? []).map((it) => (
                  <tr key={it.id} className="hover:bg-slate-50/60" data-testid={`expense-row-${it.id}`}>
                    <td className="px-5 py-3 whitespace-nowrap">{fmtDate(it.date)}</td>
                    <td className="px-5 py-3 max-w-xs">
                      <button
                        type="button"
                        onClick={() => onEdit(it)}
                        className="text-left font-medium text-slate-900 hover:underline truncate cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
                        data-testid={`expense-title-${it.id}`}
                        title="Gideri düzenle"
                      >
                        {it.description}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{it.category}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${it.expense_type === "general" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-sky-50 text-sky-700 border border-sky-200"}`}>
                        {TYPE_LABEL[it.expense_type] || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {it.customer_name ? (
                        <>
                          <div className="text-slate-800">{it.customer_name}</div>
                          <div className="text-xs text-slate-500">{it.job_name || "—"}</div>
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3">{CASH_LABEL[it.cash_account] || it.cash_account}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-rose-600 font-medium">{fmtTRY(it.amount)}</td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setViewing(it)} data-testid={`expense-view-${it.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(it)} data-testid={`expense-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onDelete(it)} data-testid={`expense-delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </SectionCard>

      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Gider Detayı</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-2 text-sm" data-testid="expense-detail">
              <DetailRow label="Tarih" value={fmtDate(viewing.date)} />
              <DetailRow label="Açıklama" value={viewing.description} />
              <DetailRow label="Kategori" value={viewing.category} />
              <DetailRow label="Gider Türü" value={TYPE_LABEL[viewing.expense_type]} />
              <DetailRow label="Tutar" value={fmtTRY(viewing.amount)} />
              <DetailRow label="Para Birimi" value={viewing.currency || "TRY"} />
              <DetailRow label="Kasa" value={CASH_LABEL[viewing.cash_account] || viewing.cash_account} />
              <DetailRow label="Müşteri" value={viewing.customer_name || "—"} />
              <DetailRow label="İş / Proje" value={viewing.job_name || "—"} />
              <DetailRow label="Not" value={viewing.notes || "—"} />
              <DetailRow label="Oluşturulma" value={viewing.created_at ? fmtDate(viewing.created_at.slice(0, 10)) : "—"} />
              <DetailRow label="Güncellenme" value={viewing.updated_at ? fmtDate(viewing.updated_at.slice(0, 10)) : "—"} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryRow({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="tabular-nums font-semibold text-right text-slate-900" data-testid={testId}>{value}</dd>
    </div>
  );
}

function ExpenseCard({ it, onView, onEdit, onDelete }) {
  return (
    <li className="flex items-start gap-1 pl-4 pr-1 py-2.5 active:bg-slate-50" data-testid={`expense-card-${it.id}`}>
      <button type="button" onClick={onEdit} className="flex-1 min-w-0 text-left py-0.5" data-testid={`expense-card-open-${it.id}`}>
        <div className="flex items-center gap-2 text-[11px] text-slate-500 min-w-0">
          <span className="tabular-nums shrink-0">{fmtDate(it.date)}</span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md font-medium truncate ${it.expense_type === "general" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-sky-50 text-sky-700 border border-sky-200"}`}>
            {TYPE_LABEL[it.expense_type] || "—"}
          </span>
        </div>
        <div className="flex items-start justify-between gap-2 mt-0.5">
          <span className="font-medium text-slate-900 leading-snug break-words line-clamp-2 min-w-0">{it.description}</span>
          <span className="shrink-0 tabular-nums text-rose-600 font-semibold" data-testid={`expense-card-amount-${it.id}`}>{fmtTRY(it.amount)}</span>
        </div>
        <div className="text-xs text-slate-500 truncate">
          {it.category} · {CASH_LABEL[it.cash_account] || it.cash_account}
        </div>
        {it.customer_name && (
          <div className="text-xs text-slate-500 truncate">{it.customer_name}{it.job_name ? ` · ${it.job_name}` : ""}</div>
        )}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost" className="shrink-0 h-10 w-10" aria-label="İşlemler" data-testid={`expense-card-menu-${it.id}`}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onSelect={onView} data-testid={`expense-card-view-${it.id}`}><Eye className="w-4 h-4 mr-2" /> Detay</DropdownMenuItem>
          <DropdownMenuItem onSelect={onEdit} data-testid={`expense-card-edit-${it.id}`}><Pencil className="w-4 h-4 mr-2" /> Düzenle</DropdownMenuItem>
          <DropdownMenuItem onSelect={onDelete} className="text-red-600 focus:text-red-600" data-testid={`expense-card-delete-${it.id}`}><Trash2 className="w-4 h-4 mr-2" /> Sil</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right">{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, required, disabled, type = "text", step, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ""} type={type} step={step} onChange={(e) => onChange(e.target.value)} required={required} disabled={disabled} className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}
